<template>
  <div class="schedules-list">
    <div v-if="trainings.length" class="py-6 pt-0 mb-4 overflow-x-auto">
      <table class="schedules-table">
        <thead>
          <tr>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th1') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th2') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th3') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th4') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th5') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th6') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th7') }}</th>
            <th class="px-1 schedules-table-header">{{ $t('schedule.list.table.ths.th8') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(training, index) in trainings" :key="`training-${index}`">
            <td class="schedules-table-data">
              <div
                v-if="training.classNote"
                @click="openModalNote(training.classNote)"
                class="flex items-center justify-center"
              >
                <VSvgIcon name="information" class="top-0 w-5 ml-1 text-center text-red-600" />
              </div>
              <span v-else>-</span>
            </td>
            <td class="schedules-table-data">{{ formatDate(training.trainingTime) }}</td>
            <td class="schedules-table-data">{{ formatTime(training.trainingTime) }}</td>
            <td class="schedules-table-data">{{ training.machine }}</td>
            <td class="schedules-table-data">
              <span v-if="training.trainingTopic">{{ training.trainingTopic }}</span>
              <span v-else>-</span>
            </td>
            <td class="schedules-table-data">{{ training.trainingType }}</td>
            <td class="schedules-table-data">
              <a
                v-if="training.classLink"
                :href="training.classLink"
                target="_blank"
                class="p-1 px-5 font-semibold text-white bg-blue-500 rounded-full hover:bg-blue-700 transition-colors duration-75"
              >
                Join
              </a>
              <span v-else>-</span>
            </td>
            <td class="schedules-table-data">
              <button class="font-semibold text-blue-500" @click="handleClickCancel(training.id)">
                {{ $t('schedule.list.table.btn') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="schedules-no-data">
      {{ $t('schedule.list.noTrainings') }}
    </div>
    <button @click="showForm = true" class="py-5 mx-auto rounded-full button">
      {{ $t('schedule.list.btn') }}
    </button>

    <Modal
      :show="showTooltip"
      max-width="600px"
      width="95%"
      @close="showTooltip = false"
      theme="dark"
    >
      <div class="p-16 text-xl text-center text-black bg-white">
        {{ classNote }}
      </div>
    </Modal>

    <!-- schedule new training modal -->
    <Modal :show="showForm" max-width="600px" width="95%" theme="dark">
      <ScheduleForm
        :excludedTimes="excludedTimes"
        @close="showForm = false"
        @next="handleClickNext"
      />
    </Modal>

    <!-- trigger/confirmation schedule modal -->
    <ScheduleModal
      :show="showSchedule"
      @close="
        showSchedule = false;
        showConfirmCancel = true;
      "
      @proceed="handleScheduleTraining"
      :loading="dispatching"
    >
      <template v-slot:default>
        <div>{{ $t('schedule.modalConfirm.box.lines.l1') }}</div>
        <div class="font-bold">{{ scheduleDateTime }}</div>
      </template>
      <template v-slot:info>
        <p class="py-5 text-sm font-light text-center">
          {{ $t('schedule.modalConfirm.additionalText') }}
        </p>
      </template>
      <template v-slot:button>{{ $t('schedule.modalConfirm.btn') }}</template>
    </ScheduleModal>

    <!-- trigger/confirmation cancel schedule modal -->
    <ScheduleModal
      :show="showConfirmCancel"
      :isCancel="true"
      @proceed="showConfirmCancel = false"
      @close="
        showConfirmCancel = false;
        showSchedule = true;
      "
      :loading="dispatching"
    >
      <template v-slot:default>
        <div>{{ $t('schedule.modalConfirmCancelShedule.box.lines.l1') }}</div>
        <div class="font-bold">{{ scheduleDateTime }}</div>
      </template>
      <template v-slot:info>
        <p class="py-5 text-lg font-bold text-center">
          {{ $t('schedule.modalConfirmCancelShedule.additionalText') }}
        </p>
      </template>
      <template v-slot:button>{{ $t('schedule.modalConfirmCancelShedule.btn') }}</template>
    </ScheduleModal>

    <!-- trigger/confirmation cancel modal -->
    <ScheduleModal
      :show="showCancel"
      :isCancel="true"
      @proceed="handleCancelTraining(training.id)"
      @close="showCancel = false"
      :loading="dispatching"
    >
      <template v-slot:default>
        <div>{{ $t('schedule.modalCancel.box.lines.l1', { machine: machineSeries }) }}</div>
        <div class="font-bold">{{ cancelDateTime }}</div>
        <div>{{ $t('schedule.modalCancel.box.lines.l2') }}</div>
      </template>
    </ScheduleModal>
  </div>
</template>

<script>
import { parseISO, format } from 'date-fns';

import Modal from '@/components/shared/modal/Modal';
import ScheduleModal from './ScheduleModal';
import ScheduleForm from './ScheduleForm';
import VSvgIcon from '@/components/base/VSvgIcon';

export default {
  name: 'ScheduleList',
  components: { Modal, ScheduleModal, ScheduleForm, VSvgIcon },
  props: { trainings: Array },
  data() {
    return {
      classNote: '',
      showTooltip: false,
      showCancel: false,
      showConfirmCancel: false,
      showForm: false,
      showSchedule: false,
      training: null,
      trainingParams: null,
      dispatching: false,
    };
  },
  computed: {
    machineSeries() {
      return this.training ? this.training.machine.split('-')[0] : '';
    },
    cancelDateTime() {
      return this.training ? this.formatDateTime(this.training.trainingTime) : '';
    },
    scheduleDateTime() {
      return this.trainingParams ? this.formatDateTime(this.trainingParams.time) : '';
    },
    excludedTimes() {
      return this.trainings.map(t => t.trainingId);
    },
  },
  methods: {
    openModalNote(text) {
      this.classNote = text;
      this.showTooltip = true;
    },
    formatDateTime(dateStr) {
      const date = parseISO(dateStr);
      const formattedTime = format(date, "EEE, MMM d yyy 'at' h:mm aaaa '( EASTERN TIME )'");
      return formattedTime;
    },
    formatDate(dateStr) {
      return format(parseISO(dateStr), 'MMM d, yyy');
    },
    formatTime(dateStr) {
      return format(parseISO(dateStr), "h:mm aaaa '( EASTERN TIME )'");
    },
    handleClickCancel(trainingId) {
      this.showCancel = true;
      this.training = this.trainings.find(({ id }) => id === trainingId);
    },
    async handleCancelTraining(trainingId) {
      this.dispatching = true;
      await this.$store.dispatch('customer/cancelTraining', { trainingId });
      this.showCancel = false;
      this.dispatching = false;
    },
    handleClickNext({ slotId, groupId, phone, time }) {
      this.trainingParams = { slotId, groupId, phone, time };
      this.showForm = false;
      this.showSchedule = true;
    },
    handleCloseScheduleTraining() {
      confirm();
      this.showSchedule = false;
    },
    async handleScheduleTraining() {
      try {
        this.dispatching = true;
        const { slotId, groupId, phone } = this.trainingParams;
        await this.$store.dispatch('customer/scheduleTraining', { slotId, groupId, phone });
        this.resetScheduleForm();
      } catch (error) {
        this.$notify({
          group: 'error',
          type: 'error',
          title: 'ATTENTION',
          text: error.response.data.message,
        });
        this.resetScheduleForm();
      }
    },
    resetScheduleForm() {
      this.trainingParams = null;
      this.showSchedule = false;
      this.dispatching = false;
    },
  },
};
</script>
